import { forwardRef } from 'react'
import type { SelectHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  options: Array<{ value: string; label: string }>
  placeholder?: string
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, options, placeholder, id, ...props }, ref) => {
    const selectId = id ?? label?.toLowerCase().replace(/\s/g, '-')
    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-[var(--color-text-secondary)]"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            id={selectId}
            className={cn(
              'flex h-11 w-full appearance-none rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 pr-8 text-sm text-[var(--color-text-primary)] transition-colors duration-200 ease-[var(--ease-smooth)] focus:outline-none focus:border-[var(--color-accent-amber)]/50 focus:ring-1 focus:ring-[var(--color-accent-amber)]/30 disabled:cursor-not-allowed disabled:opacity-50 sm:h-10',
              error && 'border-[var(--color-accent-red)]',
              className,
            )}
            ref={ref}
            {...props}
          >
            {placeholder && (
              <option value="" className="bg-[var(--color-bg-elevated)]">
                {placeholder}
              </option>
            )}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} className="bg-[var(--color-bg-elevated)]">
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
        </div>
        {error && (
          <p className="text-xs text-[var(--color-accent-red)]">{error}</p>
        )}
      </div>
    )
  },
)
Select.displayName = 'Select'

export { Select }
